import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import {
  coalesceFacilityNetworkList,
  isEmptyFacilityNetworkListStatus,
} from "./facilityNetworkListFetch";

export type UnlinkRequestItem = {
  id: number;
  initiator_facility_id: number;
  counterparty_facility_id: number;
  initiator_role: "parent" | "child";
  status: "pending" | "approved" | "rejected" | "cancelled" | "expired";
  reason?: string | null;
  resolution_note?: string | null;
  expires_at?: string | null;
  resolved_at?: string | null;
  created_at?: string | null;
  facility_name: string;
  facility_npi?: string | null;
  facility_ccn?: string | null;
  facility_type?: string | null;
  facility_state?: string | null;
  facility_role: string;
};

function apiBase(): string {
  const baseUrl = getApiOrigin().replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error("REACT_APP_API_BASEURL is not configured.");
  }
  return baseUrl;
}

async function parseJsonResponse(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text();
  return text ? (JSON.parse(text) as Record<string, unknown>) : {};
}

function extractError(json: Record<string, unknown>, response: Response): string {
  const detail = json.detail;
  if (detail && typeof detail === "object" && detail !== null) {
    const message = (detail as Record<string, unknown>).message;
    if (typeof message === "string") return message;
  }
  if (typeof json.message === "string") return json.message;
  if (typeof json.detail === "string") return json.detail;
  return `Request failed with status ${response.status}`;
}

function unwrapData<T>(json: Record<string, unknown>): T {
  const data = json.data;
  if (data && typeof data === "object") return data as T;
  return json as T;
}

export async function listUnlinkRequests(
  facilityId: number,
  direction: "initiated" | "received",
): Promise<UnlinkRequestItem[]> {
  const url = `${apiBase()}/api/v1/facility-management/facility/${facilityId}/unlink-requests?direction=${direction}`;
  return coalesceFacilityNetworkList(url, async () => {
    const response = await authenticatedFetch(url);
    if (isEmptyFacilityNetworkListStatus(response.status)) return [];
    const json = await parseJsonResponse(response);
    if (!response.ok) throw new Error(extractError(json, response));
    const data = unwrapData<{ requests?: UnlinkRequestItem[] }>(json);
    return Array.isArray(data.requests) ? data.requests : [];
  });
}

export async function createUnlinkRequest(
  facilityId: number,
  options?: { counterpartyFacilityId?: number; reason?: string },
): Promise<UnlinkRequestItem> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${facilityId}/unlink-requests`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        counterparty_facility_id: options?.counterpartyFacilityId,
        reason: options?.reason,
      }),
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return unwrapData<UnlinkRequestItem>(json);
}

export async function approveUnlinkRequest(
  facilityId: number,
  requestId: number,
  resolutionNote?: string,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${facilityId}/unlink-requests/${requestId}/approve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resolution_note: resolutionNote }),
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Unlink request approved.",
  };
}

export async function rejectUnlinkRequest(
  facilityId: number,
  requestId: number,
  resolutionNote?: string,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${facilityId}/unlink-requests/${requestId}/reject`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resolution_note: resolutionNote }),
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Unlink request rejected.",
  };
}

export async function cancelUnlinkRequest(
  facilityId: number,
  requestId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${facilityId}/unlink-requests/${requestId}/cancel`,
    { method: "POST" },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Unlink request cancelled.",
  };
}

export async function resendUnlinkRequest(
  facilityId: number,
  requestId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${facilityId}/unlink-requests/${requestId}/resend`,
    { method: "POST" },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Unlink request resent.",
  };
}
